import classNames from "classnames";
import AnyFilter from "PFApp/components/filters/any_filter";
import { useModalRegionRef } from "PFCore/hooks/use_modal_region_ref";
import { Id } from "PFTypes";
import { FilterV2, FilterValueV2 } from "PFTypes/filters_v2";
import { RefObject, useCallback, useMemo } from "react";

import { getFilterOptionsQuery } from "../../helpers/get_filter_options_query";
import { FilterV2WithIdentifierAndUpdater } from "../../stores/filters_v2_store/filters_v2_store.types";
import css from "./filters_v2_list.module.scss";

type FilterV2ElementProps = {
  filter: FilterV2;
  onUpdate: (filter: FilterV2WithIdentifierAndUpdater) => void;
  searchId?: Id;
  lastFocusedFilterNameRef?: RefObject<string | null>;
  disabled?: boolean;
  narrow?: boolean;
  wide?: boolean;
};

export const FilterV2Element = ({
  filter,
  onUpdate,
  searchId,
  lastFocusedFilterNameRef,
  wide,
  disabled
}: FilterV2ElementProps) => {
  const FilterComponent = filter.InputComponent || AnyFilter;
  const modalRegionRef = useModalRegionRef();

  const onFocus = () => {
    if (lastFocusedFilterNameRef && lastFocusedFilterNameRef.current !== filter.name) {
      lastFocusedFilterNameRef.current = filter.name;
    }
  };

  const onBlur = () => {
    if (lastFocusedFilterNameRef && lastFocusedFilterNameRef.current) {
      lastFocusedFilterNameRef.current = null;
    }
  };

  const handleChange = useCallback(
    (value: FilterValueV2) => {
      onUpdate({ name: filter.name, isChildren: filter.isChildren, value });
    },
    [filter.isChildren, filter.name, onUpdate]
  );

  const handleError = useCallback(
    (error?: string | null) => {
      onUpdate({ name: filter.name, isChildren: filter.isChildren, error });
    },
    [filter.isChildren, filter.name, onUpdate]
  );

  const legacyFilterWithQuery = useMemo(
    () => ({ query: getFilterOptionsQuery(filter, searchId), ...filter }) as unknown as FilterV2,
    [filter, searchId]
  );

  return (
    <li
      className={classNames(css.item, css[filter.name], {
        [css.wide]: wide || filter.inputStyle?.fullWidth
      })}
      data-qa-id={`filters--${filter.name}`}
    >
      <FilterComponent
        filter={legacyFilterWithQuery}
        handleChange={handleChange}
        autofocus={lastFocusedFilterNameRef?.current === filter.name}
        searchId={searchId}
        handleFocus={onFocus}
        handleBlur={onBlur}
        disabled={disabled || filter.disabled}
        onValidate={handleError}
        portalRef={modalRegionRef}
      />
    </li>
  );
};
